import { createSlice } from '@reduxjs/toolkit';
import {
    getAllClients,
    getClientSummary,
    getClientInfo,
    updateClientSystemSettings,
    updateClientLogo,
    updateClientOrganisationSettings,
    addPaymentInfo,
    addNote,
    deleteNote,
    deploymentUsers,
    toggleUser,
    addNewDeploymentUser,
    deleteDeployment,
    transferDeploymentId,
    editNote,
    processReactivateDeployment,
    processDeIdentifyDeployment,
    processNotifyClient
} from 'api/clients';
import { accessAsAdmin } from 'api/users';
import { dispatch } from '../index';
import { store } from 'store';

const initialState = {
    list: [],
    total: 0,
    currentPage: 1,
    rowsPerPage: 10,
    isLoading: false,
    searchQuery: '',
    showDisabled: false,
    tags: [],
    sorting: {
        field: 'currentEndDate',
        value: -1 // 1 = asc; -1 = desc
    },
    renewalDateFilter: {
        startDate: null,
        endDate: null
    },
    summary: {
        active: 0,
        inactive: 0,
        new: 0,
        isLoading: false
    },
    selectedClient: {
        data: null,
        isLoading: false
    },
    logoUploading: false,
    users: [],
    showTrial: false,
    showOnlyInactive: false,
    showOnlyDeidentified: false
};

const slice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        setClientsList(state, action) {
            state.list = action.payload.data;
            state.total = action.payload.total;
            state.isLoading = false;
        },
        setClientCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        setClientRowsPerPage(state, action) {
            state.rowsPerPage = action.payload;
        },
        setClientLoading(state, action) {
            state.isLoading = action.payload;
        },
        setClientSummaryLoading(state, action) {
            state.summary.isLoading = action.payload;
        },
        setClientSummary(state, action) {
            state.summary = action.payload.data;
            state.summary.isLoading = false;
        },
        setSearchQuery(state, action) {
            state.currentPage = 1; // reset to page 1
            state.searchQuery = action.payload;
        },
        setShowDisabled(state, action) {
            state.currentPage = 1; // reset to page 1
            state.showDisabled = action.payload;
        },
        setTags(state, action) {
            state.currentPage = 1; // reset to page 1
            state.tags = action.payload;
        },
        setRenewalDateFilter(state, action) {
            state.renewalDateFilter = action.payload;
        },
        setSelectedClient(state, action) {
            state.selectedClient = action.payload;
        },
        setLogoUploading(state, action) {
            state.logoUploading = action.payload;
        },
        resetClientState(state) {
            return { ...initialState, showTrial: state.showTrial }; // prevent from resetting showTrial flag
        },
        setUsers(state, action) {
            state.users = action.payload;
        },
        setTrialDisplay(state, action) {
            state.showTrial = action.payload;
        },
        setSorting(state, action) {
            state.currentPage = 1; // reset to page 1
            state.sorting = action.payload;
        },
        setShowOnlyInactive(state, action) {
            state.showOnlyInactive = action.payload;
        },
        setShowOnlyDeidentified(state, action) {
            state.showOnlyDeidentified = action.payload;
        }
    }
});

export default slice.reducer;

// fetch all clients list
export function fetchClientsList() {
    return async () => {
        await getListClients();
    };
}

// clients table pagination
export function clientCurrentPage(val) {
    return async () => {
        await dispatch(slice.actions.setClientCurrentPage(val));
        await getListClients();
    };
}
export function setClientSorting(val) {
    return async () => {
        dispatch(slice.actions.setSorting(val));
        await getListClients();
    };
}

export function setTrialList(val) {
    return async () => {
        dispatch(slice.actions.setTrialDisplay(val));
    };
}

export function clientRowsPerPage(val) {
    return async () => {
        dispatch(slice.actions.setClientRowsPerPage(val));
    };
}

// clients summary
export function fetchClientsSummary() {
    return async () => {
        await getListClients();
        try {
            dispatch(slice.actions.setClientSummaryLoading(true));
            const storeData = store.getState();
            const { showTrial } = storeData.clients;

            const summary = await getClientSummary(showTrial);
            dispatch(slice.actions.setClientSummary(summary));
        } catch (err) {
            console.log(err);
            dispatch(slice.actions.setClientLoading(false));
            dispatch(slice.actions.setClientSummaryLoading(false));
            throw new Error(err.message);
        }
    };
}

// clients list search query
export function searchClientsList(query) {
    return async () => {
        dispatch(slice.actions.setSearchQuery(query));
        await getListClients();
    };
}

// clients list toggle for disabled clients
export function showDisabledClients(val) {
    return async () => {
        dispatch(slice.actions.setShowDisabled(val));
        await getListClients();
    };
}

// clients list toggle for tags
export function searchClientsTags(val) {
    return async () => {
        dispatch(slice.actions.setTags(val));
        await getListClients();
    };
}

// clients list advanced filtering
export function searchAdvancedFiltering(tags, renewalDateFilter, showOnlyInactive, showOnlyDeidentified) {
    return async () => {
        dispatch(slice.actions.setTags(tags));
        dispatch(slice.actions.setRenewalDateFilter(renewalDateFilter));
        dispatch(slice.actions.setShowOnlyInactive(showOnlyInactive));
        dispatch(slice.actions.setShowOnlyDeidentified(showOnlyDeidentified));
        await getListClients();
    };
}

// initial load clients list data, reset all the filters
export function initialLoadClientData() {
    return async () => {
        await dispatch(slice.actions.resetClientState());
        await getListClients();
    };
}

// set selected client for deployment info modal
export function setSelectedClient(client) {
    return async () => {
        await getDeployment(client.deployment);
        await getUsers(client.deployment);
    };
}

// update client settings
export function updateClientSettings(val) {
    return async () => {
        try {
            const storeData = store.getState();
            const { deployment } = storeData.clients.selectedClient.data;
            await updateClientSystemSettings(val);
            await getDeployment(deployment);
        } catch (e) {
            console.log(e);
            throw new Error(err.message);
        }
    };
}

// update organisation settings
export function updateOrganisationSettings(val) {
    return async () => {
        try {
            const storeData = store.getState();
            const { deployment } = storeData.clients.selectedClient.data;
            await updateClientOrganisationSettings(val);
            await getDeployment(deployment);
        } catch (e) {
            console.log(e);
            throw new Error(err.message);
        }
    };
}

// upload new logo
export function uploadNewClientLogo(deployment, file) {
    return async () => {
        try {
            dispatch(slice.actions.setLogoUploading(true));
            await updateClientLogo(deployment, file);
            dispatch(slice.actions.setLogoUploading(false));
        } catch (err) {
            dispatch(slice.actions.setLogoUploading(false));
            console.log(err);
            throw new Error(err.message);
        }
    };
}

// add new payment info
export function addNewPaymentInfo(deployment, data) {
    return async () => {
        try {
            await addPaymentInfo(deployment, data);
            await getDeployment(deployment);
        } catch (err) {
            console.log(err);
            throw new Error(err.message);
        }
    };
}

// add new note
export function addNewNote(deployment, data) {
    return async () => {
        try {
            await addNote(deployment, data);
            await getDeployment(deployment);
        } catch (err) {
            console.log(err);
        }
    };
}

// update note
export function updateNote(deployment, data) {
    return async () => {
        try {
            await editNote(deployment, data);
            await getDeployment(deployment);
        } catch (err) {
            console.log(err);
        }
    };
}

// delete note
export function removeNote(deployment, id) {
    return async () => {
        try {
            await deleteNote(deployment, id);
            await getDeployment(deployment);
        } catch (err) {
            console.log(err);
            throw new Error(err.message);
        }
    };
}

// get all users in a deployment
export function getAllDeploymentUsers(deployment) {
    return async () => {
        try {
            const users = await deploymentUsers(deployment);
            dispatch(slice.actions.setUsers(users.data));
        } catch (err) {
            console.log(err);
        }
    };
}

// toggle deployment user status
export function toggleDeploymentUser(deployment, data) {
    return async () => {
        try {
            await toggleUser(deployment, data);
            await getUsers(deployment);
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

// create new user
export function createNewDeploymentUser(deployment, data) {
    return async () => {
        try {
            await addNewDeploymentUser(deployment, data);
            await getUsers(deployment);
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

export function transferDeployment(source, destination) {
    return async () => {
        try {
            console.log('Transferring deployment from', source, 'to', destination);
            await transferDeploymentId(source, destination);

            await getDeployment(destination);
            getListClients();
        } catch (err) {
            console.log(err);
        }
    };
}

// soft delete deployment
export function softDeleteDeployment(deployment) {
    return async () => {
        try {
            await deleteDeployment(deployment);
            await getListClients();
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

// access deployment as admin
export function accessDeploymentAsAdmin(data) {
    return async () => {
        try {
            return await accessAsAdmin(data);
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

// soft delete deployment
export function reactivateDeployment(deployment) {
    return async () => {
        try {
            await processReactivateDeployment(deployment);
            await getListClients();
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

// de-identify deployment
export function deIdentifyDeployment(deployment) {
    return async () => {
        try {
            await processDeIdentifyDeployment(deployment);
            await getListClients();
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

// notify client
export function notifyClient(deployment) {
    return async () => {
        try {
            await processNotifyClient(deployment);
            await getListClients();
        } catch (err) {
            throw new Error(err.message);
        }
    };
}

// fetch all clients list function
async function getListClients() {
    try {
        dispatch(slice.actions.setClientLoading(true));
        const storeData = store.getState();
        const {
            currentPage,
            sorting,
            rowsPerPage,
            searchQuery,
            showDisabled,
            tags,
            renewalDateFilter,
            showTrial,
            showOnlyInactive,
            showOnlyDeidentified
        } = storeData.clients;

        const clients = await getAllClients({
            perPage: rowsPerPage,
            pageNo: currentPage,
            query: searchQuery,
            showDisabled,
            tags,
            startDate: renewalDateFilter?.startDate || '',
            endDate: renewalDateFilter?.endDate || '',
            isTrial: showTrial,
            sortField: sorting?.field,
            sorting: sorting.value,
            showOnlyInactive,
            showOnlyDeidentified
        });
        dispatch(slice.actions.setClientsList(clients?.data));
    } catch (err) {
        console.log(err);
        dispatch(slice.actions.setClientLoading(false));
        throw new Error(err.message);
    }
}

async function getDeployment(deployment) {
    try {
        dispatch(slice.actions.setSelectedClient({ data: {}, isLoading: true }));
        const clientInfo = await getClientInfo(deployment);
        dispatch(slice.actions.setSelectedClient({ data: clientInfo?.data, isLoading: false }));
    } catch (error) {
        console.error('Failed to fetch client info:', error);
        throw new Error(err.message);
    }
}

async function getUsers(deployment) {
    try {
        const users = await deploymentUsers(deployment);
        dispatch(slice.actions.setUsers(users.data));
    } catch (error) {
        console.error('Failed to fetch client users:', error);
        throw new Error(err.message);
    }
}
